import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import { dishes } from "../data/dishes";
import { useTheme, useDocumentTitle } from "../hooks/useSite";
import { Host } from "../illustrations";

// Existing club sign-up integration: the form posts to the café's Google
// Apps Script (sheet) and emails the team through EmailJS. Field names are
// kept so both destinations keep working.
const SHEET_URL = "https://script.google.com/macros/s/AKfycbx3MQeRqUtOnIsuS6V-0tQSIO8nNzU6h9hNPf3ZC2vQ57M_KoInxJlIXIvWazfYjFQI/exec";
const EMAILJS = { service: "service_zleknzbbn", template: "template_962ouwi", key: "JiNk4fjq66o6LTiKN" };

export default function ClubPage() {
  useTheme(dishes[3]);
  useDocumentTitle("RollOver club");
  const formRef = useRef(null);
  const [status, setStatus] = useState({ state: "idle" });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const required = { required: "This field is required." };

  const submit = async () => {
    setStatus({ state: "sending" });
    const results = await Promise.allSettled([
      fetch(SHEET_URL, { method: "POST", body: new FormData(formRef.current) }),
      emailjs.sendForm(EMAILJS.service, EMAILJS.template, formRef.current, EMAILJS.key),
    ]);
    const sheet = results[0].status === "fulfilled";
    const mail = results[1].status === "fulfilled";
    if (sheet || mail) {
      setStatus({ state: "sent", sheet, mail });
      reset();
    } else setStatus({ state: "error" });
  };

  return (
    <>
      <section className="ro-education-intro ro-form-intro">
        <div className="ro-container ro-split">
          <div>
            <h1>
              <strong className="ro-italic">Join the</strong>
              <br />
              <span className="ro-outline">RollOver club.</span>
            </h1>
            <p className="ro-education-intro__sub">(A little joy on your birthday.)</p>
          </div>
          <Host className="ro-form-intro__figure" />
        </div>
      </section>
      <section className="ro-section">
        <div className="ro-container ro-container--narrow">
          <p className="ro-lead" style={{ marginBottom: 40 }}>
            Share your name, number and birthday. The café keeps the list for offers, event news and the ambassador programme.
          </p>
          {status.state === "sent" ? (
            <div className="ro-form-result" role="status">
              <h2>
                You’re <span className="ro-outline">on the list.</span>
              </h2>
              <p>
                {status.sheet && status.mail
                  ? "Your details were saved to the café’s list and emailed to the team."
                  : status.mail
                  ? "Your details were emailed to the team. The list save could not be confirmed."
                  : "Your details were saved to the café’s list. The email to the team could not be confirmed."}
              </p>
              <button type="button" className="ro-pill ro-pill--solid" onClick={() => setStatus({ state: "idle" })}>
                Add another person
              </button>
            </div>
          ) : (
            <form ref={formRef} className="ro-form" onSubmit={handleSubmit(submit)} noValidate>
              <div className="ro-form__grid">
                <label className={`ro-field ${errors.name ? "is-invalid" : ""}`}>
                  <span className="ro-field__label">Full name</span>
                  <input type="text" autoComplete="name" {...register("name", required)} />
                  {errors.name && <span className="ro-field__error">{errors.name.message}</span>}
                </label>
                <label className={`ro-field ${errors.subject ? "is-invalid" : ""}`}>
                  <span className="ro-field__label">Phone number</span>
                  <input type="tel" autoComplete="tel" {...register("subject", required)} />
                  {errors.subject && <span className="ro-field__error">{errors.subject.message}</span>}
                </label>
                <label className={`ro-field ${errors.email ? "is-invalid" : ""}`}>
                  <span className="ro-field__label">E-mail</span>
                  <input type="email" autoComplete="email" {...register("email", { ...required, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid e-mail." } })} />
                  {errors.email && <span className="ro-field__error">{errors.email.message}</span>}
                </label>
                <label className={`ro-field ${errors.date ? "is-invalid" : ""}`}>
                  <span className="ro-field__label">Date of birth</span>
                  <input type="date" autoComplete="bday" {...register("date", required)} />
                  {errors.date && <span className="ro-field__error">{errors.date.message}</span>}
                </label>
              </div>
              <div className="ro-form__actions">
                <button type="submit" className="ro-pill ro-pill--solid" disabled={status.state === "sending"}>
                  {status.state === "sending" ? "Submitting…" : "Join the club"}
                </button>
                <p className="ro-small" role="status" aria-live="polite">
                  {status.state === "error" ? "Could not submit. Please try again later." : "Your details go to the café’s sign-up sheet and are emailed to the team."}
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
